/**
 * App Bootstrap — startup sequence.
 *
 * Startup lifecycle:
 *
 *   1. Concurrently:
 *        a. Load history (IDB on web / SQLite on native) → hydrate Zustand
 *           Active sessions get status = 'recovering' immediately.
 *           UI appears as soon as Zustand is hydrated.
 *        b. Initialize TorrentEngine (DHT bootstrap, NAT traversal)
 *
 *   2. Wire DI (history + engine → coordinator)
 *
 *   3. Recovery manager (waits for BOTH 1a and 1b to complete):
 *        - Sessions with status recovering|transferring|connecting|seeding
 *          are re-added to the engine from resumeData or magnetUri.
 *        - On reattach, the engine emits events → Zustand transitions naturally.
 *        - If no magnet or resume data → mark failed.
 *        - Paused/completed/failed sessions are left as-is.
 *
 * Critical invariants:
 *   - History hydration MUST NOT wait for engine initialization.
 *   - Recovery MUST wait for both history and engine before starting.
 *   - requestPermission() for web directory handles is NEVER called here.
 *     It requires user interaction — the UI is responsible for triggering it.
 */

import { initDb } from '@share/storage';
import { createHistoryRepository } from '@share/storage';
import { useTransferStore } from '../../core/state/transfer.store';
import { TransferCoordinator } from '../../core/services/transfer-coordinator';
import { AppStorageEngine } from '@share/storage';
import { ShareEngine } from '@share/networking';
import { TelemetryService } from '@share/telemetry';
import { getPlatformDestinationProvider } from '@share/platform';
import type { PersistedSession } from '@share/storage';

const WEB_DESTINATION_PREFIX = 'web-directory:';

function decodeBase64(base64: string): Uint8Array {
  if (typeof atob === 'function') {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
  const BufferCtor = (globalThis as any).Buffer;
  if (BufferCtor) {
    return Uint8Array.from(BufferCtor.from(base64, 'base64'));
  }
  throw new Error('Base64 decoder unavailable');
}

/** Load history and hydrate Zustand. Active sessions are set to 'recovering'. */
async function hydrateStore(history: ReturnType<typeof createHistoryRepository>): Promise<void> {
  const sessions = await history.getAllSessions();

  const ACTIVE_STATUSES = new Set(['transferring', 'connecting', 'seeding', 'recovering']);

  for (const s of sessions) {
    // Skip sessions with no usable identity at all — they cannot be recovered
    if (!s.torrentIdentity?.v1 && !s.torrentIdentity?.v2 && !s.magnetUri) {
      TelemetryService.logInfo('bootstrap', `Clearing unrecoverable legacy session ${s.id}`);
      history.deleteSession(s.id).catch(console.error);
      continue;
    }

    useTransferStore.getState().addSession({
      id: s.id,
      torrentIdentity: s.torrentIdentity,
      // engineTorrentId omitted — runtime-only, rebuilt by registerRecoveredSession after reattachment
      direction: s.direction,
      files: s.files,
      // Active sessions get 'recovering' — truthful about state until engine reattaches
      status: ACTIVE_STATUSES.has(s.status) ? 'recovering' : s.status,
      progress: s.progress,
      speed: 0,          // runtime-only, reconstructed from engine
      uploadSpeed: 0,
      peersCount: 0,
      seedersCount: 0,
      bytesTransferred: s.bytesTransferred,
      bytesUploaded: s.bytesUploaded,
      totalBytes: s.totalBytes,
      eta: 0,
      magnetUri: s.magnetUri,
      destinationRef: s.destinationRef,
      revision: s.revision,
      createdAt: s.createdAt,
      updatedAt: s.updatedAt,
    });
  }

  TelemetryService.logInfo('bootstrap', `Hydrated ${sessions.length} sessions from history`);
}

async function resolveRecoveryDestination(
  session: PersistedSession,
  destinationProvider: ReturnType<typeof getPlatformDestinationProvider>
) {
  if (
    typeof window !== 'undefined' &&
    session.destinationRef?.startsWith(WEB_DESTINATION_PREFIX)
  ) {
    try {
      const { IDBHandles } = require('@share/storage');
      const permission = await IDBHandles.checkPermission(session.destinationRef);

      if (permission !== 'granted') {
        TelemetryService.logInfo(
          'bootstrap',
          `Web destination for ${session.id} requires user re-authorization (${permission})`
        );
        return null;
      }

      const handle = await IDBHandles.getHandle(session.destinationRef);
      if (!handle) {
        TelemetryService.logInfo(
          'bootstrap',
          `Stored web destination handle missing for ${session.id}`
        );
        return null;
      }

      return { kind: 'browser-directory' as const, handle };
    } catch (err) {
      TelemetryService.logError(
        'bootstrap',
        `Error resolving IDB destination handle for ${session.id}`,
        err
      );
      return null;
    }
  }

  if (session.destinationRef) {
    return { kind: 'native-path' as const, path: session.destinationRef };
  }

  // No destinationRef stored — try platform default.
  // On web, this always throws ("Browser requires user to explicitly choose...").
  // On mobile, this returns a native Downloads path.
  const suggestedName = session.files[0]?.name || 'share-download';
  try {
    return await destinationProvider.defaultDestination(suggestedName);
  } catch {
    // Web: no default destination is possible without user gesture.
    // Downgrade session to 'paused' so it's visible in the list and
    // the user can manually resume/re-download it. Do NOT mark failed —
    // all session data (magnetUri, files, progress) is still intact.
    TelemetryService.logInfo(
      'bootstrap',
      `No auto-destination for web session ${session.id} — marking paused for user to resume`
    );
    return null;
  }
}

/** Recover interrupted sessions — must be called AFTER both hydrateStore and engine init. */
async function recoverSessions(history: ReturnType<typeof createHistoryRepository>): Promise<void> {
  const interrupted = await history.getInterruptedSessions();
  const destinationProvider = getPlatformDestinationProvider();

  await Promise.allSettled(
    interrupted.map(async (session: PersistedSession) => {
      try {
        const destination = await resolveRecoveryDestination(session, destinationProvider);
        if (!destination) {
          // Web: no auto-destination available — downgrade to 'paused' so the
          // session is visible in the list with a clear, non-error status.
          // Set recoveryState='permission-required' (separate from status) so the
          // UI can show a "Re-authorize" prompt without polluting TransferStatus.
          useTransferStore.getState().updateSession(session.id, {
            status: 'paused',
            recoveryState: 'permission-required',
            speed: 0,
            uploadSpeed: 0,
            revision: (session.revision ?? 0) + 1,
            updatedAt: Date.now(),
          });
          await history.saveSession({
            ...session,
            status: 'paused',
            revision: (session.revision ?? 0) + 1,
            updatedAt: Date.now(),
          } as any);
          return;
        }

        if (session.resumeData) {
          try {
            TelemetryService.logInfo('bootstrap', `Restoring ${session.id} from resume data`);
            const resumeBytes = decodeBase64(session.resumeData.data);
            const handle = await ShareEngine.restoreFromResumeData(resumeBytes, destination);
            // Engine is now authoritative — register the correlation ID so events route correctly
            TransferCoordinator.registerRecoveredSession(session.id, handle.engineTorrentId);
            return;
          } catch (resumeErr) {
            TelemetryService.logError(
              'bootstrap',
              `Failed to restore ${session.id} from resume data; falling back to magnet URI`,
              resumeErr
            );
            // Fall through to magnetUri recovery path below
          }
        }

        if (session.direction === 'send') {
          TelemetryService.logInfo(
            'bootstrap',
            `Cannot recover send session ${session.id} from magnet alone`
          );
          const now = Date.now();
          useTransferStore.getState().updateSession(session.id, {
            status: 'failed',
            error: 'Sharing session could not be resumed without engine resume data.',
            revision: session.revision + 1,
            updatedAt: now,
          });
          await history.saveSession({
            ...session,
            status: 'failed',
            error: 'Sharing session could not be resumed without engine resume data.',
            revision: session.revision + 1,
            updatedAt: now,
          } as any);
          return;
        }

        if (session.magnetUri) {
          TelemetryService.logInfo('bootstrap', `Re-adding ${session.id} from magnet URI`);
          const handle = await ShareEngine.download({ magnetUri: session.magnetUri }, destination);
          // Register recovered session in sessionMap after engine reattachment
          TransferCoordinator.registerRecoveredSession(session.id, handle.engineTorrentId);
          return;
        }

        // No recovery path available — mark as failed
        TelemetryService.logInfo('bootstrap', `No recovery data for ${session.id} — marking failed`);
        const now = Date.now();
        useTransferStore.getState().updateSession(session.id, {
          status: 'failed',
          error: 'Cannot recover: no magnet URI or resume data',
          revision: session.revision + 1,
          updatedAt: now,
        });
        await history.saveSession({
          ...session,
          status: 'failed',
          error: 'Cannot recover: no magnet URI or resume data',
          revision: session.revision + 1,
          updatedAt: now,
        } as any);

      } catch (err) {
        TelemetryService.logError(
          'bootstrap',
          `Failed to restore session ${session.id}`,
          err
        );
        // Handle gracefully: avoid sticking in 'recovering' indefinitely
        const isPermissionErr =
          err instanceof Error &&
          (err.name === 'NotAllowedError' ||
           err.name === 'SecurityError' ||
           err.message.toLowerCase().includes('permission') ||
           err.message.toLowerCase().includes('user-activation'));

        const recoveryState = isPermissionErr ? 'permission-required' : 'none';
        const now = Date.now();

        useTransferStore.getState().updateSession(session.id, {
          status: 'paused',
          recoveryState,
          speed: 0,
          uploadSpeed: 0,
          error: isPermissionErr ? undefined : (err instanceof Error ? err.message : String(err)),
          revision: (session.revision ?? 0) + 1,
          updatedAt: now,
        });
        await history.saveSession({
          ...session,
          status: 'paused',
          revision: (session.revision ?? 0) + 1,
          updatedAt: now,
        } as any).catch(() => {});
      }
    })
  );
}

/**
 * Reattach paused receive sessions to the engine after page reload.
 *
 * Paused sessions are NOT in getInterruptedSessions() (they were intentionally
 * stopped). But after reload, engineTorrentId is gone so the user cannot
 * pause/cancel/resume them. This function reattaches them WITHOUT auto-resuming.
 *
 * Ordering: register session FIRST (so events route correctly), then pause.
 * This prevents a window where the engine torrent exists but the coordinator
 * has no mapping for it.
 *
 * fsa-chunk-store (OPFS) already holds previously-downloaded pieces, so the
 * engine will resume from where it left off once the user manually resumes.
 */
async function recoverPausedSessions(history: ReturnType<typeof createHistoryRepository>): Promise<void> {
  const paused = await history.getPausedReceiveSessions();
  const destinationProvider = getPlatformDestinationProvider();

  await Promise.allSettled(
    paused.map(async (session: PersistedSession) => {
      try {
        if (!session.magnetUri) {
          TelemetryService.logInfo('bootstrap', `Paused session ${session.id} has no magnet URI — skipping reattach`);
          return;
        }

        const destination = await resolveRecoveryDestination(session, destinationProvider);

        if (!destination) {
          // Permission not yet granted — set recoveryState so UI can prompt.
          // Status stays 'paused' — session data is intact.
          useTransferStore.getState().updateSession(session.id, {
            recoveryState: 'permission-required',
          });
          TelemetryService.logInfo('bootstrap', `Paused session ${session.id} needs permission re-authorization`);
          return;
        }

        TelemetryService.logInfo('bootstrap', `Reattaching paused session ${session.id} to engine`);
        const handle = await ShareEngine.download({ magnetUri: session.magnetUri }, destination);

        // ── Register FIRST, then pause ─────────────────────────────────────────
        // Engine event routing depends on engineTorrentId → sessionId mapping.
        // Pause must happen AFTER registration or events during the pause window
        // would be unroutable (engine has torrent, coordinator has no mapping).
        TransferCoordinator.registerRecoveredSession(session.id, handle.engineTorrentId);
        await ShareEngine.pause(handle.engineTorrentId);

        // Clear recoveryState — session is now live and controllable.
        useTransferStore.getState().updateSession(session.id, {
          recoveryState: 'none',
          engineTorrentId: handle.engineTorrentId,
        });

        TelemetryService.logInfo('bootstrap', `Paused session ${session.id} reattached (engine id: ${handle.engineTorrentId})`);
      } catch (err) {
        TelemetryService.logError('bootstrap', `Failed to reattach paused session ${session.id}`, err);
        // Leave as 'paused' — data intact, user can retry manually.
      }
    })
  );
}

export async function bootstrapApp(): Promise<void> {
  const timer = TelemetryService.startTimer('bootstrap');

  try {
    // Create the correct history repository for this platform
    const history = createHistoryRepository();

    // ── 1. Concurrent initialization ────────────────────────────────────────
    // History hydration and engine init run concurrently.
    // The UI renders as soon as hydrateStore() completes (Zustand is updated).
    // Engine init (DHT, NAT) happens in parallel without blocking the UI.

    await Promise.all([
      // 1a. Init DB schema (if needed) then hydrate Zustand
      (async () => {
        await initDb();
        TelemetryService.logInfo('bootstrap', 'Storage initialized');
        await hydrateStore(history);
      })(),

      // 1b. Init torrent engine
      (async () => {
        TelemetryService.logInfo('bootstrap', 'Engine initializing');
        // ShareEngine initialization is handled by the engine module's own lazy init
        // (nothing explicit to call here in the current architecture)
      })(),
    ]);

    // ── 2. Wire DI ───────────────────────────────────────────────────────────
    TransferCoordinator.initialize(history, ShareEngine as any);
    TelemetryService.logInfo('bootstrap', 'TransferCoordinator initialized');

    // ── 3. Recovery (fire-and-forget — does NOT block app render) ────────────
    //
    // Sessions are already in Zustand as 'recovering' from hydrateStore().
    // The UI is fully ready to render at this point.
    //
    // Recovery re-attaches the engine to P2P swarms (connects to trackers,
    // resolves peers, etc.) which is inherently async and can take 5-30 seconds.
    // Blocking the app on this makes the page feel "frozen" to the user.
    //
    // Instead: fire recovery immediately in the background.
    // The TransferCard list already shows sessions with status='recovering'
    // and updates naturally when the engine connects and emits events.
    //
    // recoverSessions:       recovering | transferring | connecting | seeding
    // recoverPausedSessions: paused receive sessions (reattach WITHOUT auto-resume)
    recoverSessions(history).catch((err) => {
      TelemetryService.logError('bootstrap', 'Background recovery error', err);
    });
    recoverPausedSessions(history).catch((err) => {
      TelemetryService.logError('bootstrap', 'Background paused-session reattachment error', err);
    });

    TelemetryService.logInfo('bootstrap', 'Bootstrap complete');
  } catch (err) {
    TelemetryService.logError('bootstrap', 'Bootstrap failed', err);
    throw err;
  } finally {
    timer();
  }
}
